// src/constants/merchantApi.js

/* ============================================================
   Sohocard — Merchant API (public documentation)
   Only merchant-facing endpoints, all backed by edge functions.
   Sections: Authentication · Products · Orders · Transactions
   ============================================================ */

export const endpointGroups = [
    { id: 'getting-started', name: 'Getting Started', description: 'Setup and authentication' },
    { id: 'products', name: 'Products', description: 'Browse the catalog' },
    { id: 'orders', name: 'Orders', description: 'Create and manage orders' },
    { id: 'transactions', name: 'Transactions', description: 'Query transaction history' },
];

export const endpoints = [
    /* ── GETTING STARTED ─────────────────────────── */
    {
        id: 'getting-started',
        group: 'getting-started',
        title: 'Getting Started',
        type: 'documentation',
        content: `
# Sohocard Merchant API

**Base URL:** \`https://api.sohocard.ir/functions/v1\`

**Required headers on every request:**
- \`x-api-key: <your merchant key>\` — issued by Sohocard support
- \`Authorization: Bearer <user JWT>\` — from the login endpoint (except where noted)

**Amounts:** products no longer carry a fixed price. The merchant submits a *requested amount* when creating an order. Sohocard confirms availability and quotes the final price.

**Rate limit:** 100 requests/minute per API key. Requests over the limit return \`429\`.
        `.trim(),
    },

    /* ── AUTH ────────────────────────────────────── */
    {
        id: 'merchant-login',
        group: 'getting-started',
        title: 'Merchant Login',
        method: 'POST',
        path: '/merchant-login',
        description: 'Exchange merchant credentials for a JWT used on all subsequent calls.',
        parameters: [
            { name: 'email', type: 'string', required: true },
            { name: 'password', type: 'string', required: true },
        ],
        headers: [
            { name: 'x-api-key', required: true },
            { name: 'Content-Type', required: true, value: 'application/json' },
        ],
        successResponse: {
            code: 200,
            example: `{
  "access_token": "eyJhbGciOi...",
  "refresh_token": "...",
  "expires_in": 3600,
  "merchant": { "id": "uuid", "email": "merchant@example.com" }
}`,
        },
        errorResponses: [
            { code: 401, description: 'Invalid email or password' },
            { code: 403, description: 'API key not authorized for merchants' },
        ],
    },

    /* ── PRODUCTS ────────────────────────────────── */
    {
        id: 'list-products',
        group: 'products',
        title: 'List Products',
        method: 'POST',
        path: '/merchant-products-list',
        description: 'Returns the active product catalog. Supports category and brand filters.',
        parameters: [
            { name: 'category', type: 'string', required: false, description: 'gift_card | voucher | wallet | card_network' },
            { name: 'brand_id', type: 'string', required: false },
            { name: 'search', type: 'string', required: false },
            { name: 'page', type: 'int', required: false, default: '1' },
            { name: 'page_size', type: 'int', required: false, default: '24' },
        ],
        headers: [
            { name: 'x-api-key', required: true },
            { name: 'Authorization', required: true },
        ],
        successResponse: {
            code: 200,
            example: `{
  "items": [
    {
      "sku": "apple-25-us",
      "title": "Apple Gift Card $25",
      "kind": "gift_card",
      "category": "gift_card",
      "currency": "USD",
      "region": "us",
      "brand": { "id": "uuid", "name": "Apple", "slug": "apple" }
    }
  ],
  "total": 42,
  "page": 1,
  "page_size": 24
}`,
        },
        errorResponses: [
            { code: 401, description: 'Missing/invalid JWT' },
            { code: 429, description: 'Rate limit exceeded' },
        ],
    },

    {
        id: 'get-product',
        group: 'products',
        title: 'Get Product',
        method: 'POST',
        path: '/merchant-products-get',
        description: 'Returns a single product by SKU.',
        parameters: [
            { name: 'sku', type: 'string', required: true },
        ],
        headers: [
            { name: 'x-api-key', required: true },
            { name: 'Authorization', required: true },
        ],
        successResponse: {
            code: 200,
            example: `{
  "sku": "apple-25-us",
  "title": "Apple Gift Card $25",
  "description": null,
  "kind": "gift_card",
  "category": "gift_card",
  "currency": "USD",
  "region": "us",
  "stock": null,
  "brand": { "id": "uuid", "name": "Apple", "slug": "apple" }
}`,
        },
        errorResponses: [
            { code: 404, description: 'Product not found' },
        ],
    },

    /* ── ORDERS ──────────────────────────────────── */
    {
        id: 'create-order',
        group: 'orders',
        title: 'Create Order',
        method: 'POST',
        path: '/merchant-orders-create',
        description: 'Creates a pending order with a requested amount. Sohocard quotes the final price and returns it via callback or by polling the order status.',
        parameters: [
            { name: 'sku', type: 'string', required: true, description: 'Product SKU' },
            { name: 'requested_amount', type: 'number', required: true, description: 'Amount the merchant wants' },
            { name: 'requested_currency', type: 'string', required: true, description: 'USD | EUR | GBP | TRY | AUD | AZN' },
            { name: 'destination', type: 'string', required: true, description: 'Wallet id / card number to deliver to' },
            { name: 'callback_url', type: 'string', required: false, description: 'https URL for status updates' },
            { name: 'metadata', type: 'object', required: false, description: 'Free-form key/value pairs' },
        ],
        headers: [
            { name: 'x-api-key', required: true },
            { name: 'Authorization', required: true },
            { name: 'Content-Type', required: true, value: 'application/json' },
        ],
        successResponse: {
            code: 201,
            example: `{
  "id": "uuid",
  "order_no": "ORD-2026-000123",
  "status": "pending_quote",
  "sku": "apple-25-us",
  "requested_amount": 100,
  "requested_currency": "USD",
  "created_at": "2026-10-14T11:20:00Z"
}`,
        },
        errorResponses: [
            { code: 400, description: 'Invalid sku, amount, or destination' },
            { code: 401, description: 'Unauthorized' },
            { code: 404, description: 'Product not found or inactive' },
            { code: 422, description: 'Amount out of allowed range for this product' },
        ],
    },

    {
        id: 'get-order',
        group: 'orders',
        title: 'Get Order',
        method: 'POST',
        path: '/merchant-orders-get',
        description: 'Returns the current state of an order including the quoted price once available.',
        parameters: [
            { name: 'order_id', type: 'string', required: false },
            { name: 'order_no', type: 'string', required: false, description: 'Either order_id or order_no, not both' },
        ],
        headers: [
            { name: 'x-api-key', required: true },
            { name: 'Authorization', required: true },
        ],
        successResponse: {
            code: 200,
            example: `{
  "id": "uuid",
  "order_no": "ORD-2026-000123",
  "status": "quoted",
  "requested_amount": 100,
  "requested_currency": "USD",
  "quoted_amount": 104.00,
  "quoted_currency": "USD",
  "quoted_at": "2026-10-14T11:25:00Z",
  "items": [
    {
      "sku": "apple-25-us",
      "title": "Apple Gift Card $25",
      "qty": 1
    }
  ]
}`,
        },
        errorResponses: [
            { code: 404, description: 'Order not found' },
        ],
    },

    {
        id: 'list-orders',
        group: 'orders',
        title: 'List Orders',
        method: 'POST',
        path: '/merchant-orders-list',
        description: 'Returns orders belonging to the merchant, paginated.',
        parameters: [
            { name: 'status', type: 'string', required: false, description: 'pending_quote | quoted | paid | delivered | failed | cancelled' },
            { name: 'from_date', type: 'string', required: false, description: 'ISO date' },
            { name: 'to_date', type: 'string', required: false, description: 'ISO date' },
            { name: 'page', type: 'int', required: false, default: '1' },
            { name: 'page_size', type: 'int', required: false, default: '50' },
        ],
        headers: [
            { name: 'x-api-key', required: true },
            { name: 'Authorization', required: true },
        ],
        successResponse: {
            code: 200,
            example: `{
  "items": [
    { "id": "uuid", "order_no": "ORD-…", "status": "paid", "total": 104.00, "currency": "USD" }
  ],
  "total": 128,
  "page": 1,
  "page_size": 50
}`,
        },
        errorResponses: [{ code: 401, description: 'Unauthorized' }],
    },

    /* ── TRANSACTIONS ────────────────────────────── */
    {
        id: 'list-transactions',
        group: 'transactions',
        title: 'List Transactions',
        method: 'POST',
        path: '/merchant-transactions-list',
        description: 'Returns merchant transaction ledger — deposits, order payments, refunds.',
        parameters: [
            { name: 'type', type: 'string', required: false, description: 'deposit | order_payment | refund | fee' },
            { name: 'status', type: 'string', required: false, description: 'pending | completed | failed' },
            { name: 'currency', type: 'string', required: false },
            { name: 'from_date', type: 'string', required: false },
            { name: 'to_date', type: 'string', required: false },
            { name: 'page', type: 'int', required: false, default: '1' },
            { name: 'page_size', type: 'int', required: false, default: '50' },
        ],
        headers: [
            { name: 'x-api-key', required: true },
            { name: 'Authorization', required: true },
        ],
        successResponse: {
            code: 200,
            example: `{
  "items": [
    {
      "id": "uuid",
      "type": "order_payment",
      "status": "completed",
      "amount": 104.00,
      "currency": "USD",
      "order_no": "ORD-…",
      "description": "Payment for order ORD-…",
      "created_at": "2026-10-14T11:30:00Z"
    }
  ],
  "total": 342,
  "page": 1,
  "page_size": 50
}`,
        },
        errorResponses: [{ code: 401, description: 'Unauthorized' }],
    },

    {
        id: 'get-transaction',
        group: 'transactions',
        title: 'Get Transaction',
        method: 'POST',
        path: '/merchant-transactions-get',
        description: 'Returns a single transaction by id.',
        parameters: [
            { name: 'transaction_id', type: 'string', required: true },
        ],
        headers: [
            { name: 'x-api-key', required: true },
            { name: 'Authorization', required: true },
        ],
        successResponse: {
            code: 200,
            example: `{
  "id": "uuid",
  "type": "order_payment",
  "status": "completed",
  "amount": 104.00,
  "currency": "USD",
  "order_id": "uuid",
  "order_no": "ORD-…",
  "created_at": "2026-10-14T11:30:00Z"
}`,
        },
        errorResponses: [
            { code: 404, description: 'Transaction not found' },
        ],
    },
];